/**
 * PanelBody — the header panel's tab bar + active-tab content orchestrator.
 * SPEC-stakeholder-portal-presentation-redesign-2026-07-20, R3.
 *
 * AC5: fetches the capability-derived panel-config in PARALLEL with the
 * default tab's own content query -- `MySubmissionsTab` mounts and fires its
 * own `/issues` fetch immediately and unconditionally here; it is NEVER
 * gated behind panel-config resolving first (no serial round-trip). While
 * config is loading/unavailable, the tab bar renders the FAIL-CLOSED base
 * client tabs (My Submissions + Roadmap) so the panel never flashes empty
 * and never speculatively offers a super-admin tab before the gate confirms
 * it (AC5's "coordination/all-issues DATA is never returned to a
 * non-super-admin" -- the underlying /api/coordination endpoints stay
 * super-admin-gated regardless, but the TAB never even appears to invite the
 * attempt for anyone panel-config didn't clear).
 *
 * R4 AC4: also threads `is_super_admin` from this SAME panel-config read into
 * RoadmapTab, so a super-admin's Roadmap tab gets the epic drill-down expand
 * affordance while a plain client's identical tab never renders it.
 */
import { useMemo } from 'react';
import { ViewTabs, type ViewTab } from '@/components/ui/view-tabs';
import { usePortalPanelConfig, PANEL_CONFIG_FALLBACK } from '@/hooks/usePortalPanelConfig';
import { usePortalPanel, type PortalPanelTab } from '@/lib/portal/PortalPanelContext';
import { MySubmissionsTab } from '@/components/portal/panel/MySubmissionsTab';
import { RoadmapTab } from '@/components/portal/panel/RoadmapTab';
import { CoordinationTab } from '@/components/portal/panel/CoordinationTab';
import { AllIssuesTab } from '@/components/portal/panel/AllIssuesTab';

const TAB_LABELS: Record<PortalPanelTab, string> = {
  my_submissions: 'My Submissions',
  roadmap: 'Roadmap',
  coordination: 'Coordination',
  all_issues: 'All Issues',
};

interface PanelBodyProps {
  /** Whether the caller has resolve_issues, threaded to My Submissions' confirm loop. */
  canResolve: boolean;
  /**
   * Which shell hosts this body — the narrow client Popover (384px) or the
   * wider super-admin Sheet drawer (~448px). Threaded to MySubmissionsTab
   * (condensed card list vs. full DataTable — visual-review finding,
   * 2026-07-24 final pass) and used here to keep the tab bar from wrapping.
   * Defaults to 'drawer' — the historical/tested behavior for any caller
   * that predates this prop.
   */
  variant?: 'popover' | 'drawer';
}

export function PanelBody({ canResolve, variant = 'drawer' }: PanelBodyProps) {
  const { activeTab, setActiveTab } = usePortalPanel();
  // AC5: fired unconditionally alongside (not gated behind) the active tab's
  // own content query below -- both requests are in flight in parallel.
  const configQuery = usePortalPanelConfig(true);

  const tabs = (configQuery.data?.tabs ?? PANEL_CONFIG_FALLBACK.tabs) as PortalPanelTab[];

  const viewTabs: ViewTab[] = useMemo(
    () => tabs.map((t) => ({ value: t, label: TAB_LABELS[t] ?? t })),
    [tabs],
  );

  // Defensive: if the resolved config drops a tab the context still has
  // selected (e.g. a slow super-admin config resolving to fewer tabs than a
  // stale open call assumed), fall back to the first available tab rather
  // than rendering a blank body.
  const resolvedTab = tabs.includes(activeTab) ? activeTab : tabs[0];

  return (
    <div className="flex flex-col" data-testid="portal-panel-body">
      <ViewTabs
        views={viewTabs}
        currentView={resolvedTab}
        onChange={(v) => setActiveTab(v as PortalPanelTab)}
        className="px-3 pt-2"
        dense
      />
      <div className="max-h-[70vh] overflow-y-auto" role="tabpanel">
        {resolvedTab === 'my_submissions' && (
          <MySubmissionsTab canResolve={canResolve} variant={variant} />
        )}
        {resolvedTab === 'roadmap' && <RoadmapTab isSuperAdmin={configQuery.data?.is_super_admin ?? false} />}
        {resolvedTab === 'coordination' && <CoordinationTab />}
        {resolvedTab === 'all_issues' && <AllIssuesTab />}
      </div>
    </div>
  );
}
